import React, { useState, useEffect, useCallback } from 'react';
import { Button, Card, Avatar, Statistic, Typography, Space, Divider, Grid, Tag, Empty, Input, message, Upload } from 'antd';
import {
  GoogleOutlined, LogoutOutlined, UserOutlined,
  UnorderedListOutlined, HeartOutlined, RightOutlined, StarOutlined, EditOutlined, CameraOutlined, ToolOutlined,
} from '@ant-design/icons';
import { Link } from 'react-router-dom';
import AddressBook from '../components/AddressBook';
import AccountBinding from '../components/AccountBinding';
import { useCustomer } from '../context/CustomerContext';

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

const API_BASE = process.env.REACT_APP_API_BASE || '';

const BRAND = 'var(--brand-primary, #222957)';
const BORDER = '#E5E7F5';
const HEADING = '#222A5A';

const getToken = () => {
  try { return localStorage.getItem('customer_token'); } catch { return null; }
};

export default function MemberProfile() {
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const [customer, setCustomer] = useState(null);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const { refresh: refreshCustomerCtx } = useCustomer();
  // 編輯暱稱／頭像
  const [editingProfile, setEditingProfile] = useState(false);
  const [editName, setEditName] = useState('');
  const [editPicture, setEditPicture] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [orders, setOrders] = useState(null);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [wishlistCount, setWishlistCount] = useState(null);
  const [myCoupons, setMyCoupons] = useState(null); // 我的優惠券收藏夾
  const [claimCode, setClaimCode] = useState('');
  const [claiming, setClaiming] = useState(false);

  // 從網址參數處理登入導回（帶 ctoken），並取得客人資訊
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    const ctoken = q.get('ctoken');
    if (ctoken) {
      try { localStorage.setItem('customer_token', ctoken); } catch {}
      q.delete('ctoken');
      const rest = q.toString();
      window.history.replaceState(null, '', window.location.pathname + (rest ? `?${rest}` : ''));
    }
    const token = ctoken || getToken();
    if (!token) { setCheckingAuth(false); return; }
    fetch(`${API_BASE}/api/auth/google/customer/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : null))
      .then((info) => {
        if (info) setCustomer(info);
        else { try { localStorage.removeItem('customer_token'); } catch {} }
      })
      .catch(() => {})
      .finally(() => setCheckingAuth(false));
  }, []);

  // 載入訂單以計算摘要（訂單數、累計消費）
  const loadOrders = useCallback(() => {
    const token = getToken();
    if (!token) return;
    setLoadingOrders(true);
    fetch(`${API_BASE}/api/bracelet-orders/my`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : { items: [] }))
      .then((d) => setOrders(d.items || []))
      .catch(() => setOrders([]))
      .finally(() => setLoadingOrders(false));
  }, []);

  // 載入願望清單數量
  const loadWishlistCount = useCallback(() => {
    const token = getToken();
    if (!token) return;
    fetch(`${API_BASE}/api/wishlist`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => setWishlistCount(Array.isArray(d) ? d.length : 0))
      .catch(() => setWishlistCount(null));
  }, []);

  // 載入我的優惠券收藏夾
  const loadCoupons = useCallback(() => {
    const token = getToken();
    if (!token) return;
    fetch(`${API_BASE}/api/coupons/mine`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => setMyCoupons(Array.isArray(d) ? d : []))
      .catch(() => setMyCoupons([]));
  }, []);

  useEffect(() => {
    if (customer) { loadOrders(); loadWishlistCount(); loadCoupons(); }
  }, [customer, loadOrders, loadWishlistCount, loadCoupons]);

  // 自行輸入券碼收藏
  const claimCoupon = async () => {
    const token = getToken();
    const codeV = claimCode.trim();
    if (!token || !codeV) return;
    setClaiming(true);
    try {
      const res = await fetch(`${API_BASE}/api/coupons/claim`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ code: codeV }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { message.error(data.detail || '收藏失敗'); return; }
      message.success(data.message || '已收藏優惠券');
      setClaimCode('');
      loadCoupons();
    } catch {
      message.error('收藏失敗，請稍後再試');
    } finally {
      setClaiming(false);
    }
  };

  const startCustomerLogin = useCallback(() => {
    window.location.href = `${API_BASE}/api/auth/google/customer/login?next=${encodeURIComponent('/member')}`;
  }, []);

  const logoutCustomer = useCallback(() => {
    try { localStorage.removeItem('customer_token'); } catch {}
    window.location.reload();
  }, []);

  // 編輯個人資料
  const openEditProfile = () => {
    setEditName(customer?.name || '');
    setEditPicture(customer?.picture || '');
    setEditingProfile(true);
  };
  const uploadAvatar = async (file) => {
    setUploadingAvatar(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${API_BASE}/api/upload/image`, { method: 'POST', body: fd });
      if (!res.ok) throw new Error();
      const { url } = await res.json();
      if (url) setEditPicture(url);
    } catch { message.error('頭像上傳失敗'); }
    finally { setUploadingAvatar(false); }
    return false;
  };
  const saveProfile = async () => {
    setSavingProfile(true);
    try {
      const res = await fetch(`${API_BASE}/api/auth/google/customer/profile`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({ name: editName.trim(), picture: editPicture }),
      });
      if (!res.ok) throw new Error();
      const d = await res.json();
      setCustomer((c) => ({ ...c, name: d.name, picture: d.picture }));
      refreshCustomerCtx();
      setEditingProfile(false);
      message.success('已更新個人資料');
    } catch { message.error('更新失敗，請稍後再試'); }
    finally { setSavingProfile(false); }
  };

  const containerStyle = {
    maxWidth: isMobile ? 600 : 1160,
    margin: '0 auto',
    padding: isMobile ? '16px' : '28px 32px 48px',
    position: 'relative',
    zIndex: 1,
  };

  // 尚在驗證登入
  if (checkingAuth) {
    return (
      <div style={{ ...containerStyle, textAlign: 'center', paddingTop: 80 }}>
        <Space direction="vertical" size="large">
          <Avatar size={64} icon={<UserOutlined />} />
          <Text type="secondary">載入中…</Text>
        </Space>
      </div>
    );
  }

  // 未登入：顯示登入提示（沿用我的訂單的登入樣式）
  if (!customer) {
    return (
      <div style={containerStyle}>
        <div style={{ textAlign: 'center', marginBottom: 24, paddingTop: 24 }}>
          <Title level={3} style={{ color: '#222A5A', fontWeight: 800 }}>會員中心</Title>
          <Text type="secondary">用 Google 登入後，即可查看您的個人資料與訂單</Text>
        </div>
        <div style={{ textAlign: 'center' }}>
          <Button
            type="primary" size="large" icon={<GoogleOutlined />}
            onClick={startCustomerLogin}
            style={{ height: 48, fontSize: 16 }}
          >
            使用 Google 登入
          </Button>
        </div>
      </div>
    );
  }

  const orderCount = orders ? orders.length : 0;
  const totalSpent = orders
    ? orders.reduce((sum, o) => (o.payment_status === 'paid' && o.amount ? sum + o.amount : sum), 0)
    : 0;

  const linkRow = (to, icon, label, count) => (
    <Link to={to} style={{ textDecoration: 'none' }} className="member-link-row">
      <div
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '14px 8px', borderRadius: 10, transition: 'background .15s ease',
        }}
      >
        <Space size="middle">
          <span style={{ color: BRAND, fontSize: 18 }}>{icon}</span>
          <Text style={{ color: HEADING, fontSize: 15 }}>{label}</Text>
        </Space>
        <Space size="small">
          {count != null && (
            <Text type="secondary" style={{ fontSize: 14 }}>{count}</Text>
          )}
          <RightOutlined style={{ color: '#bfbfbf' }} />
        </Space>
      </div>
    </Link>
  );

  // 桌機用大型導覽磚
  const navTile = (to, icon, label, desc, count) => (
    <Link to={to} style={{ textDecoration: 'none' }} className="member-nav-tile">
      <div
        style={{
          display: 'flex', flexDirection: 'column', gap: 10, height: '100%',
          padding: 22, borderRadius: 16, border: `1px solid ${BORDER}`,
          background: 'linear-gradient(135deg, #F9FAFD 0%, #ffffff 65%)',
          transition: 'transform .18s ease, box-shadow .18s ease',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            width: 44, height: 44, borderRadius: 12, background: '#EFF0F8', color: BRAND, fontSize: 22,
          }}>{icon}</span>
          {count != null && (
            <span style={{ fontSize: 22, fontWeight: 800, color: BRAND }}>{count}</span>
          )}
        </div>
        <div>
          <div style={{ fontSize: 16, fontWeight: 700, color: HEADING }}>{label}</div>
          {desc ? <Text type="secondary" style={{ fontSize: 13 }}>{desc}</Text> : null}
        </div>
      </div>
    </Link>
  );

  // 個人資料卡（桌機置左、手機置頂）
  const profileCard = (
    <Card
      style={{
        marginBottom: isMobile ? 16 : 0, borderRadius: 16, borderColor: BORDER,
        background: 'linear-gradient(135deg, #F9FAFD 0%, #ffffff 60%)',
        ...(isMobile ? {} : { position: 'sticky', top: 24 }),
      }}
      bodyStyle={{ padding: 24 }}
    >
      {editingProfile ? (
        <div style={{ textAlign: 'center' }}>
          <div style={{ position: 'relative', display: 'inline-block', marginBottom: 12 }}>
            <Avatar size={isMobile ? 68 : 88} src={editPicture || undefined} icon={<UserOutlined />}
              style={{ background: BRAND }}>
              {editName ? editName.charAt(0) : null}
            </Avatar>
            <Upload accept="image/*" showUploadList={false} beforeUpload={uploadAvatar}>
              <Button size="small" shape="circle" icon={<CameraOutlined />} loading={uploadingAvatar}
                style={{ position: 'absolute', right: -4, bottom: -4 }} />
            </Upload>
          </div>
          <Input value={editName} onChange={(e) => setEditName(e.target.value)} maxLength={30}
            placeholder="暱稱" style={{ marginBottom: 12, maxWidth: 260, margin: '0 auto 12px' }} />
          <Space>
            <Button type="primary" loading={savingProfile} onClick={saveProfile}
              style={{ background: BRAND, borderColor: BRAND }}>儲存</Button>
            <Button onClick={() => setEditingProfile(false)}>取消</Button>
          </Space>
        </div>
      ) : (
        <>
          <div style={isMobile ? undefined : { textAlign: 'center' }}>
            <Space size="large" align="center" direction={isMobile ? 'horizontal' : 'vertical'} style={{ width: '100%', justifyContent: 'center' }}>
              <Avatar
                size={isMobile ? 68 : 88}
                src={customer.picture || undefined}
                icon={<UserOutlined />}
                style={{ background: BRAND, boxShadow: '0 6px 16px rgba(34,41,87,0.28)' }}
              >
                {customer.name ? customer.name.charAt(0) : null}
              </Avatar>
              <div style={isMobile ? undefined : { textAlign: 'center' }}>
                <div style={{ fontSize: 19, fontWeight: 800, color: HEADING }}>
                  {customer.name || '會員'}
                </div>
                <Text type="secondary" style={{ fontSize: 13 }}>{customer.email}</Text>
                <div>
                  <Button type="link" size="small" icon={<EditOutlined />} onClick={openEditProfile} style={{ padding: 0, marginTop: 2 }}>
                    編輯暱稱／頭像
                  </Button>
                </div>
              </div>
            </Space>
          </div>
          {!isMobile && (
            <Button block size="large" icon={<LogoutOutlined />} onClick={logoutCustomer} style={{ height: 46, marginTop: 22 }}>
              登出
            </Button>
          )}
        </>
      )}
    </Card>
  );

  // 優惠券折扣文字
  const couponDesc = (c) => {
    if (c.type === 'percentage') return `${c.percent || 0}% 折扣`;
    if (c.type === 'fixed') return `折 NT$ ${c.amount || 0}`;
    if (c.type === 'threshold') return `滿 NT$ ${c.min_spend || 0} 折 NT$ ${c.amount || 0}`;
    if (c.type === 'free_shipping') return '免運費';
    if (c.type === 'first_order') return `首單折 NT$ ${c.amount || 0}`;
    return c.description || '優惠券';
  };

  // 我的優惠券收藏夾（取代原本的訂單數/累計消費統計）
  const couponWallet = (
    <Card
      title={<span style={{ fontWeight: 800, color: BRAND }}>我的優惠券</span>}
      style={{ borderRadius: 16, borderColor: BORDER }}
      styles={{ body: { padding: isMobile ? 14 : 18 } }}
    >
      {/* 自行輸入券碼收藏 */}
      <Space.Compact style={{ width: '100%', marginBottom: 14 }}>
        <Input
          placeholder="輸入優惠碼收藏"
          value={claimCode}
          onChange={(e) => setClaimCode(e.target.value)}
          onPressEnter={claimCoupon}
          allowClear
        />
        <Button type="primary" loading={claiming} onClick={claimCoupon} disabled={!claimCode.trim()}>收藏</Button>
      </Space.Compact>

      {myCoupons === null ? (
        <Text type="secondary">載入中…</Text>
      ) : myCoupons.length === 0 ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="還沒有優惠券，輸入券碼即可收藏" style={{ margin: '8px 0' }} />
      ) : (
        <Space direction="vertical" size={10} style={{ width: '100%' }}>
          {myCoupons.map((c) => {
            const dim = !c.usable;
            const period = c.starts_at && c.ends_at
              ? `${c.starts_at.slice(0, 10)} ~ ${c.ends_at.slice(0, 10)}`
              : c.ends_at ? `${c.ends_at.slice(0, 10)} 到期`
                : c.starts_at ? `${c.starts_at.slice(0, 10)} 起` : null;
            const statusTag = c.used ? <Tag>已使用</Tag>
              : c.expired ? <Tag color="default">已過期</Tag>
                : c.not_started ? <Tag color="orange">尚未開始</Tag>
                  : c.sold_out ? <Tag color="red">已領完</Tag>
                    : <Link to="/cart"><Button size="small" type="primary">去使用</Button></Link>;
            return (
              <div key={c.id} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12,
                padding: '12px 14px', borderRadius: 12,
                border: `1px dashed ${dim ? '#e0e0e0' : BRAND}`,
                background: dim ? '#fafafa' : '#F8F9FC', opacity: dim ? 0.7 : 1,
              }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 800, color: dim ? '#999' : BRAND, fontSize: 16 }}>{couponDesc(c)}</div>
                  <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 2 }}>
                    代碼 <b>{c.code}</b>
                    {c.min_spend ? ` · 滿 NT$ ${c.min_spend} 可用` : ''}
                    {c.remaining != null ? ` · 限量剩 ${c.remaining} 張` : ''}
                  </div>
                  {period && (
                    <div style={{ fontSize: 12, color: '#a99', marginTop: 2 }}>期限 {period}</div>
                  )}
                </div>
                <div style={{ flex: '0 0 auto' }}>{statusTag}</div>
              </div>
            );
          })}
        </Space>
      )}
    </Card>
  );

  const styleTag = (
    <style>{`
      .member-link-row:hover > div { background: #F9FAFD; }
      @media (hover: hover) {
        .member-nav-tile > div { transition: transform .18s ease, box-shadow .18s ease; }
        .member-nav-tile:hover > div { transform: translateY(-4px); box-shadow: 0 10px 24px rgba(34,41,87,0.16); }
      }
    `}</style>
  );

  // 手機：單欄堆疊（沿用原本樣式）
  if (isMobile) {
    return (
      <div style={containerStyle}>
        {styleTag}
        <Title level={3} style={{ color: BRAND, fontWeight: 800, paddingTop: 12, letterSpacing: 1 }}>會員中心</Title>

        {profileCard}

        <div style={{ marginBottom: 16 }}>{couponWallet}</div>

        <div style={{ marginBottom: 16 }}><AccountBinding /></div>

        <div style={{ marginBottom: 16 }}><AddressBook /></div>

        <Card style={{ marginBottom: 16, borderRadius: 16, borderColor: BORDER }} bodyStyle={{ padding: '4px 12px' }}>
          {linkRow('/orders', <UnorderedListOutlined />, '我的訂單')}
          <Divider style={{ margin: 0 }} />
          {linkRow('/wishlist', <HeartOutlined />, '願望清單／收藏', wishlistCount)}
          <Divider style={{ margin: 0 }} />
          {linkRow('/custom', <StarOutlined />, '客製手鏈')}
          <Divider style={{ margin: 0 }} />
          {linkRow('/repair', <ToolOutlined />, '維修登記')}
        </Card>

        <Button block size="large" icon={<LogoutOutlined />} onClick={logoutCustomer} style={{ height: 46 }}>
          登出
        </Button>
      </div>
    );
  }

  // 桌機：左個人資料卡 + 右統計卡與大型導覽磚
  return (
    <div style={containerStyle}>
      {styleTag}
      <Title level={2} style={{ color: BRAND, fontWeight: 800, paddingTop: 4, marginBottom: 20, letterSpacing: 1 }}>會員中心</Title>

      <div style={{ display: 'grid', gridTemplateColumns: '320px minmax(0, 1fr)', gap: 32, alignItems: 'start' }}>
        <div>{profileCard}</div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
          {couponWallet}

          <AccountBinding />

          <AddressBook />

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 20 }}>
            {navTile('/orders', <UnorderedListOutlined />, '我的訂單', '查看訂單與付款狀態', orderCount)}
            {navTile('/wishlist', <HeartOutlined />, '願望清單', '你收藏的商品', wishlistCount)}
            {navTile('/custom', <StarOutlined />, '客製手鏈', '打造專屬能量手鏈')}
            {navTile('/repair', <ToolOutlined />, '維修登記', '為手鏈申請售後維修')}
          </div>
        </div>
      </div>
    </div>
  );
}
